import React, { useEffect, useState } from "react";
import { useParams, NavLink, useNavigate } from "react-router-dom";
import validator from "validator";
import { checkResetToken, resetPassword } from "../../../services/AuthService";
import { toast } from "react-toastify";
import Utils from "../../../utils";
import { PASSWORD_RULES } from "../../../configs/AppConfig";
import { FaEye, FaEyeSlash, FaCheck, FaTimes } from "react-icons/fa";

import CustomInput from "../../../components/CustomInput";  // ✅ ADDED
import './Auth.css';
import Logo from '../../../assets/Images/Docusafe_logo.webp';

function ResetPassword() {
    const navigate = useNavigate();
    const { token } = useParams();
    const [loading, setLoading] = useState(false);
    const [isReset, setIsReset] = useState(1);

    const [passwordRules, setPasswordRules] = useState(PASSWORD_RULES);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isShowPasswordContain, setIsShowPasswordContain] = useState(false);

    const [passwordVisible, setPasswordVisible] = useState(false);
    const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

    let errorsObj = {
        password: "",
        confirm_password: "",
    };
    const [errors, setErrors] = useState(errorsObj);

    // 🌐 Verify Token on Page Load
    useEffect(() => {
        setLoading(true);
        checkResetToken({ token })
            .then(() => setLoading(false))
            .catch(() => {
                setIsReset(3);
                setLoading(false);
            });
    }, [token]);

    // 🔐 Password Validation
    const handlePassword = (e) => {
        let value = e.target.value;
        setPassword(value);

        let rule = [...passwordRules];
        const isUpper = /^(?=.*[A-Z]).*$/;
        const isLower = /^(?=.*[a-z]).*$/;
        const isNumber = /^(?=.*[0-9]).*$/;
        const isSymbol = /^(?=.*[~!@#$%^&]).*$/;

        rule[0].active = value.length >= 8 && value.length <= 15;
        rule[1].active = isUpper.test(value);
        rule[2].active = isLower.test(value);
        rule[3].active = isNumber.test(value);
        rule[4].active = isSymbol.test(value);

        setPasswordRules(rule);
    };

    // 🚀 Submit
    const onSubmit = (e) => {
        e.preventDefault();

        let error = false;
        const errorObj = { ...errorsObj };
        const isContainsSymbol = /^(?=.*[~!@#$%^&]).*$/;

        if (!password) {
            errorObj.password = "Password is required";
            error = true;
        } else if (!validator.isLength(password, { min: 8, max: 15 })) {
            errorObj.password = "Password must be 8–15 characters.";
            error = true;
        } else if (!isContainsSymbol.test(password)) {
            errorObj.password = "Password must contain a special character.";
            error = true;
        }

        if (!confirmPassword) {
            errorObj.confirm_password = "Confirm password is required";
            error = true;
        } else if (confirmPassword !== password) {
            errorObj.confirm_password = "Passwords do not match";
            error = true;
        }

        setErrors(errorObj);
        if (error) return;

        setLoading(true);
        resetPassword({ password, token })
            .then(() => {
                setIsReset(2);
                toast.success("Password reset successfully");
                navigate("/login");
            })
            .catch((err) => {
                toast.error(Utils.getErrorMessage(err));
                setLoading(false);
            });
    };

    return (
        <div className="auth-container">

            {loading && <div className="page-loading">
                <img src="/images/loader.gif" alt="loader" />
            </div>}

            <div className="auth-card">

                {/* ===================== STEP 1: Reset Password [REDACTED:password]= */}
                {isReset === 1 && (
                    <>
                        <div style={{ textAlign: "center", marginBottom: "1rem" }}>
                            <h2 className="auth-title">Reset Password</h2>
                            <p className="auth-subtext">
                                Create a new password for your account
                            </p>
                        </div>

                        <form onSubmit={onSubmit} className="auth-form">

                            {/* PASSWORD */}
                            <div className="input-wrapper password-wrapper mb-3">
                                <CustomInput
                                    label="New Password"
                                    name="password"
                                    type={passwordVisible ? "text" : "password"}
                                    value={password}
                                    onChange={handlePassword}
                                    onFocus={() => setIsShowPasswordContain(true)}
                                    error={errors.password}
                                    required={true}
                                />

                                {/* Eye toggle */}
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setPasswordVisible(!passwordVisible)}

                                >
                                    <i className={`bi ${passwordVisible ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                </button>
                            </div>

                            {/* Password Rules */}
                            {isShowPasswordContain && (
                                <div className="password-rules">
                                    <p className="rules-title">Password must contain:</p>
                                    <ul className="rules-list">
                                        {passwordRules.map((rule, i) => (
                                            <li key={i} className={rule.active ? "valid" : "invalid"}>
                                                {rule.active ? <FaCheck /> : <FaTimes />} {rule.text}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            {/* CONFIRM PASSWORD */}
                            <div className="form-group position-relative">
                                <CustomInput
                                    label="Confirm Password"
                                    name="confirmPassword"
                                    type={confirmPasswordVisible ? "text" : "password"}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    error={errors.confirm_password}
                                    required={true}
                                />

                                {/* Eye Toggle */}
                                <button
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}

                                >
                                    <i className={`bi ${confirmPasswordVisible ? 'bi-eye-slash' : 'bi-eye'}`}></i>
                                </button>
                            </div>

                            <button type="submit" className="login-btn-page w-100">
                                Reset Password
                            </button>
                        </form>
                    </>
                )}

                {/* ===================== STEP 2: Success ===================== */}
                {isReset === 2 && (
                    <div className="success-message">
                        <div className="success-icon">✓</div>
                        <h3>Password Reset Successful!</h3>
                        <p>Your password has been changed successfully.</p>

                        <NavLink to="/login" className="login-btn-page w-100">
                            Back to Login
                        </NavLink>
                    </div>
                )}

                {/* ===================== STEP 3: Link Expired ===================== */}
                {isReset === 3 && (
                    <div className="error-message">
                        <div className="error-icon">!</div>
                        <h3>Link Expired</h3>
                        <p>Your password reset link has expired.</p>

                        <NavLink to="/forgot-password" className="auth-button">
                            Request New Link
                        </NavLink>
                    </div>
                )}

            </div>
        </div>
    );
}

export default ResetPassword;
